"use client";

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Plane, Sliders, BarChart3 } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + 100% scharfe, horizontal ausgerichtete
 * UI-Komponenten im Vordergrund.
 */
export default function GenerativeUiPreview() {
  const [mode, setMode] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setMode((prev) => (prev + 1) % 3);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  const cards = [
    {
      prompt: "„Flug nach Tokio“",
      title: "BER ➔ HND",
      sub: "Gate A12 · Boarding",
      Icon: Plane,
      accent: "from-rose-500 to-orange-500",
    },
    {
      prompt: "„Umsatz Q4 zeigen“",
      title: "+42% Wachstum",
      sub: "Live-Diagramm bereit",
      Icon: BarChart3,
      accent: "from-purple-500 to-pink-500",
    },
    {
      prompt: "„Licht-Steuerung“",
      title: "Dimm-Regler 80%",
      sub: "Interaktives Widget",
      Icon: Sliders,
      accent: "from-emerald-500 to-teal-500",
    },
  ];

  const current = cards[mode];
  const Icon = current.Icon;

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{ scale: [1, 1.22, 1], opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 4.5, repeat: Infinity }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-rose-500/30 blur-3xl"
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(244,63,94,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(244,63,94,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex flex-col items-center gap-2.5 antialiased">
        <motion.div
          key={`prompt-${mode}`}
          initial={{ y: -5, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center gap-1.5 rounded-full border border-rose-400/60 bg-white px-3 py-1 text-[11px] font-bold text-neutral-800 shadow-md dark:bg-zinc-900 dark:text-neutral-100"
        >
          <Sparkles className="h-3 w-3 text-rose-500" />
          <span>{current.prompt}</span>
        </motion.div>

        <div className="min-h-[68px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="flex w-56 items-center gap-3 rounded-2xl border border-rose-400/80 bg-white p-3 shadow-[0_18px_36px_-6px_rgba(244,63,94,0.5)] dark:bg-zinc-900"
            >
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${current.accent} text-white shadow-lg`}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="font-sans text-xs font-black text-neutral-900 dark:text-white">
                  {current.title}
                </span>
                <span className="font-mono text-[10px] font-bold text-rose-600 dark:text-rose-400">
                  {current.sub}
                </span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
